/**
 * Region-move lockout.
 *
 * When a school is moved to another region's cluster, the admins who belong to
 * it are moved with it and their documents on the old cluster are quarantined.
 * A dashboard tab that was already open keeps its cached region and its
 * Firebase session, so every request it makes now reaches a cluster that no
 * longer knows the admin. A reload does not help: region resolution runs only
 * at login (`applyRegionForLogin`), never on load when a region is already
 * cached.
 *
 * So the only way back is a fresh login. Once the admin is quarantined the
 * dashboard-api answers `401 { code: "E_REGION_MOVED" }` instead of a bare
 * failure, and this module turns that into a notice and a clean sign-out, the
 * same shape as the learner app's account-closed lockout (NLM-Test issue 150).
 * The login that follows asks the Directory Service and lands on the new
 * region.
 *
 * Two triggers:
 *   - the axios error interceptor, for any request the open tab makes;
 *   - a 15-minute poll (and a check when the tab regains focus), so an idle
 *     tab is caught without waiting for the admin to click something.
 *
 * Signing out, rather than re-pointing the open tab, is deliberate. Nineteen
 * Pinia stores persist what they fetched from the old cluster; the purge in
 * `endSession` is what stops the dashboard rendering one cluster's data while
 * writing to another (see `userStore.forceRegionReauth`, which it mirrors).
 *
 * Copy lives here rather than behind `$t()`: the lockout can fire before or
 * while locale messages load, and a person being signed out must still read
 * why.
 */

import { getAuth } from "firebase/auth";
import { toast } from "vue3-toastify";
import { markRegionReauthDone } from "@/lib/runtime-url-resolver";
import { logger } from "@/utils/logger";

/** The code the dashboard-api returns for an admin moved to another region. */
export const REGION_MOVED_CODE = "E_REGION_MOVED";

/** How long the notice stays up; the sign-out runs on the same timer. */
export const NOTICE_DURATION_MS = 10000;

/** How often an open, visible tab re-checks, so an idle admin is caught. */
export const STATUS_POLL_MS = 15 * 60 * 1000;

/** The least time between two re-checks, so returning to the tab often cannot outpace the poll. */
export const MIN_RECHECK_MS = 60 * 1000;

/** The login page, flagged so it can explain what happened. */
export const LOGIN_PATH = "/auth/login?schoolMoved=1";

export const MESSAGE = "Your school has moved to a new region. Please log in again.";

/**
 * Navigation seam. A full document navigation is required (module-level state
 * such as the axios base URL and every store instance must be rebuilt), and
 * jsdom cannot stub `window.location.assign`, so tests replace this instead.
 */
export const lockout = {
  navigate(url: string): void {
    window.location.assign(url);
  },
};

/** True for an axios error whose response says the admin has been moved. */
export function isRegionMovedError(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: { code?: string } } })?.response;
  return response?.status === 401 && response?.data?.code === REGION_MOVED_CODE;
}

/** The region the API says the admin now lives in, when it said. */
export function regionFromError(error: unknown): string | null {
  const data = (error as { response?: { data?: { region?: unknown } } })?.response?.data;
  return typeof data?.region === "string" && data.region ? data.region : null;
}

let triggered = false;
let pollTimer: number | null = null;
let lastCheckedAt = 0;

/**
 * Clear the session. Every step is guarded on its own: the admin must end up
 * signed out even when Firebase is unreachable or storage is blocked.
 */
async function endSession(): Promise<void> {
  try {
    await getAuth().signOut();
  } catch {
    // Offline, or already signed out. The purge below still protects the next session.
  }

  try {
    // Broad on purpose: everything in storage was fetched from the old cluster.
    window.localStorage.clear();
    window.sessionStorage.clear();
  } catch {
    // Storage unavailable (private browsing); the navigation still drops memory.
  }

  // As userStore.forceRegionReauth does: the one-time region re-auth marker is
  // stamped back after the purge. Without it, a sign-out that failed offline
  // leaves Firebase signed in, and the router guard would replace the
  // school-moved notice with the region-routing one.
  markRegionReauthDone();

  lockout.navigate(LOGIN_PATH);
}

/**
 * Tell the admin why the dashboard is stopping, then sign them out. Safe to
 * call repeatedly; only the first call does anything, because several requests
 * are usually in flight when the first one comes back moved.
 */
export function triggerRegionMoved(region: string | null = null): void {
  if (triggered) {
    return;
  }

  triggered = true;
  stopRegionMovedWatch();

  console.info("[region-moved] admin moved, ending session", { region });
  // Beaconed on pagehide, so it outlives the navigation; support wants the region.
  try {
    logger.info("region_moved", { region });
  } catch {
    // Logging must never stop the sign-out.
  }

  try {
    toast.error(MESSAGE, {
      autoClose: NOTICE_DURATION_MS,
      hideProgressBar: true,
      position: "bottom-right",
    });
  } catch {
    // A toast that cannot render must not stop the sign-out.
  }

  window.setTimeout(() => {
    void endSession();
  }, NOTICE_DURATION_MS);
}

/**
 * Re-check an open session. Any authenticated request will do: the refusal
 * comes from the auth middleware, and the interceptor raises the lockout.
 */
async function recheck(): Promise<void> {
  if (triggered) {
    return;
  }
  if (typeof document !== "undefined" && document.visibilityState !== "visible") {
    return;
  }

  const user = getAuth().currentUser;
  if (!user) {
    return;
  }
  const now = Date.now();
  if (now - lastCheckedAt < MIN_RECHECK_MS) {
    return;
  }
  lastCheckedAt = now;

  try {
    // Imported lazily: the api module imports this one for its interceptor.
    const api = (await import("@/api")).default;
    // `admin/me` sits behind authorizeAdminRequest, which is what answers
    // E_REGION_MOVED. (`admin/single/:id` has no auth and never would.)
    await api.get("v2/access/admin/me");
  } catch {
    // If this was a move the interceptor has already acted; anything else
    // waits for the next tick.
  }
}

export function startRegionMovedWatch(): void {
  if (pollTimer !== null) {
    return;
  }

  pollTimer = window.setInterval(() => {
    void recheck();
  }, STATUS_POLL_MS);
  document.addEventListener("visibilitychange", onVisibilityChange);
}

function onVisibilityChange(): void {
  void recheck();
}

export function stopRegionMovedWatch(): void {
  if (pollTimer !== null) {
    window.clearInterval(pollTimer);
    pollTimer = null;
  }

  document.removeEventListener("visibilitychange", onVisibilityChange);
}

/** Test seam: lets a spec re-run the lockout without a page reload. */
export function resetRegionMovedForTests(): void {
  triggered = false;
  lastCheckedAt = 0;
  stopRegionMovedWatch();
}
